import React, { useContext, useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Context } from '../../../index';
import Sidebar from './sidebar';
import api from '../../../http';
import './settings.css';

import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';

const TABS = [
  { id: 'profile',  label: 'Профиль',   Icon: PersonOutlineIcon },
  { id: 'password', label: 'Пароль',    Icon: LockOutlinedIcon },
  { id: 'clients',  label: 'Клиенты',   Icon: ApartmentOutlinedIcon },
];

const Settings = observer(() => {
  const { user } = useContext(Context);
  const [activeTab, setActiveTab] = useState('profile');

  // Клиентам и сотрудникам набор вкладок одинаковый, но «Клиенты» осмысленна
  // только для роли клиент. Прячем у сотрудников.
  const tabs = TABS.filter((t) => t.id !== 'clients' || !user.isStaff);

  return (
    <div className="settings">
      <div className="settings__sidebar-wrap">
        <Sidebar />
      </div>

      <div className="settings__page">
        <header className="settings__header">
          <h1 className="settings__title">Настройки</h1>
          <p className="settings__subtitle">Личные данные, пароль и доступ</p>
        </header>

        <div className="settings__layout">
          <nav className="settings__tabs" aria-label="Разделы настроек">
            {tabs.map(({ id, label, Icon }) => (
              <button
                key={id}
                type="button"
                className={`settings__tab ${activeTab === id ? 'settings__tab--active' : ''}`}
                onClick={() => setActiveTab(id)}
              >
                <Icon sx={{ fontSize: 18 }} />
                <span>{label}</span>
              </button>
            ))}
          </nav>

          <section className="settings__panel">
            {activeTab === 'profile'  && <ProfilePanel  user={user} />}
            {activeTab === 'password' && <PasswordPanel mustChange={user.mustChangePassword} />}
            {activeTab === 'clients'  && <ClientsPanel  user={user} />}
          </section>
        </div>
      </div>
    </div>
  );
});

// =================== PROFILE ===================
const ProfilePanel = observer(({ user }) => {
  const [form, setForm] = useState({
    firstname:  user.profile?.firstname  || '',
    secondname: user.profile?.secondname || '',
    lastname:   user.profile?.lastname   || '',
    email:      user.profile?.email      || '',
  });
  const [saving, setSaving]   = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError]     = useState('');

  useEffect(() => {
    setForm({
      firstname:  user.profile?.firstname  || '',
      secondname: user.profile?.secondname || '',
      lastname:   user.profile?.lastname   || '',
      email:      user.profile?.email      || '',
    });
  }, [user.profile]);

  const onChange = (e) => {
    setForm((s) => ({ ...s, [e.target.name]: e.target.value }));
    setSuccess('');
    setError('');
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      await api.put(`v1/profiles/${user.user.id}`, form);
      user.setProfile({ ...(user.profile || {}), ...form });
      setSuccess('Данные сохранены');
    } catch (err) {
      setError(err.response?.data?.message || 'Не удалось сохранить');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className="settings-form" onSubmit={onSubmit}>
      <h2 className="settings-form__title">Личные данные</h2>

      <div className="settings-form__grid">
        <Field label="Фамилия"  name="lastname"   value={form.lastname}   onChange={onChange} />
        <Field label="Имя"      name="firstname"  value={form.firstname}  onChange={onChange} required />
        <Field label="Отчество" name="secondname" value={form.secondname} onChange={onChange} />
        <Field label="E-mail"   name="email"      value={form.email}      onChange={onChange} type="email" full />
      </div>

      <div className="settings-form__static">
        <div>
          <span className="settings-form__static-label">Логин</span>
          <span className="settings-form__static-value">{user.user?.login || '—'}</span>
        </div>
        <div>
          <span className="settings-form__static-label">Роль</span>
          <span className="settings-form__static-value">{roleLabel(user.role)}</span>
        </div>
      </div>

      {error   && <div className="settings-form__msg settings-form__msg--err">{error}</div>}
      {success && <div className="settings-form__msg settings-form__msg--ok">{success}</div>}

      <div className="settings-form__actions">
        <button type="submit" className="settings-btn" disabled={saving}>
          {saving ? 'Сохранение…' : 'Сохранить'}
        </button>
      </div>
    </form>
  );
});

// =================== PASSWORD ===================
function PasswordPanel({ mustChange }) {
  const [oldPw, setOldPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showOld, setShowOld] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (newPw.length < 8) {
      setError('Новый пароль должен быть не короче 8 символов');
      return;
    }
    if (newPw !== confirm) {
      setError('Пароли не совпадают');
      return;
    }
    setSaving(true);
    try {
      await api.post('v1/me/password', mustChange ? { newPassword: newPw } : { oldPassword: oldPw, newPassword: newPw });
      setSuccess('Пароль обновлён');
      setOldPw(''); setNewPw(''); setConfirm('');
    } catch (err) {
      setError(err.response?.data?.message || 'Не удалось изменить пароль');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className="settings-form" onSubmit={onSubmit}>
      <h2 className="settings-form__title">Смена пароля</h2>

      {mustChange && (
        <div className="settings-form__msg settings-form__msg--info">
          Администратор сбросил вам пароль. Установите новый.
        </div>
      )}

      <div className="settings-form__grid settings-form__grid--single">
        {!mustChange && (
          <PasswordField
            label="Текущий пароль"
            value={oldPw}
            onChange={(e) => setOldPw(e.target.value)}
            show={showOld}
            onToggle={() => setShowOld((v) => !v)}
            autoComplete="current-password"
          />
        )}
        <PasswordField
          label="Новый пароль"
          value={newPw}
          onChange={(e) => setNewPw(e.target.value)}
          show={showNew}
          onToggle={() => setShowNew((v) => !v)}
          autoComplete="new-password"
          hint="Минимум 8 символов"
        />
        <PasswordField
          label="Повторите новый пароль"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          show={showNew}
          onToggle={() => setShowNew((v) => !v)}
          autoComplete="new-password"
        />
      </div>

      {error   && <div className="settings-form__msg settings-form__msg--err">{error}</div>}
      {success && <div className="settings-form__msg settings-form__msg--ok">{success}</div>}

      <div className="settings-form__actions">
        <button type="submit" className="settings-btn" disabled={saving}>
          {saving ? 'Сохранение…' : 'Сменить пароль'}
        </button>
      </div>
    </form>
  );
}

// =================== CLIENTS (read-only для пользователя) ===================
const ClientsPanel = observer(({ user }) => {
  const list = user.clients || [];
  return (
    <div className="settings-form">
      <h2 className="settings-form__title">Привязанные клиенты</h2>
      <p className="settings-form__hint">
        Список клиентов, от имени которых вы можете создавать заявки и видеть заказы.
        Управление привязками выполняет администратор.
      </p>

      {list.length === 0 ? (
        <div className="settings-form__msg settings-form__msg--info">
          У вас пока нет привязанных клиентов. Обратитесь к администратору.
        </div>
      ) : (
        <ul className="settings-clients">
          {list.map((c) => (
            <li key={c.id} className="settings-clients__item">
              <ApartmentOutlinedIcon sx={{ fontSize: 18 }} />
              <span className="settings-clients__name">{c.name}</span>
              <span className="settings-clients__role">{roleInClientLabel(c.role_in_client)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
});

// =================== helpers ===================
function Field({ label, full, ...inputProps }) {
  return (
    <label className={`settings-field ${full ? 'settings-field--full' : ''}`}>
      <span className="settings-field__label">{label}</span>
      <input className="settings-field__input" {...inputProps} />
    </label>
  );
}

function PasswordField({ label, value, onChange, show, onToggle, autoComplete, hint }) {
  return (
    <label className="settings-field settings-field--full">
      <span className="settings-field__label">{label}</span>
      <div className="settings-field__wrap">
        <input
          className="settings-field__input"
          type={show ? 'text' : 'password'}
          value={value}
          onChange={onChange}
          autoComplete={autoComplete}
        />
        <button type="button" className="settings-field__toggle" onClick={onToggle} tabIndex={-1}
                aria-label={show ? 'Скрыть' : 'Показать'}>
          {show ? <VisibilityOffOutlinedIcon sx={{ fontSize: 18 }} /> : <VisibilityOutlinedIcon sx={{ fontSize: 18 }} />}
        </button>
      </div>
      {hint && <span className="settings-field__hint">{hint}</span>}
    </label>
  );
}

function roleLabel(r) {
  return { 1: 'Администратор', 2: 'Оператор', 3: 'Пользователь' }[Number(r)] || '—';
}
function roleInClientLabel(r) {
  return { owner: 'Владелец', manager: 'Менеджер', viewer: 'Просмотр' }[r] || r || '—';
}

export default Settings;
